import React, { useEffect, useState, useCallback } from "react";
import { Table, Tag, Typography, Select, Space, Button, Card } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { privateAdminGetApi } from "../../utils/apiHelper";

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "invalid", label: "Invalid" },
  { value: "opened", label: "Opened" },
  { value: "clicked", label: "Clicked" },
  { value: "bounced", label: "Bounced" },
  { value: "complained", label: "Complained" },
  { value: "unsubscribed", label: "Unsubscribed" },
];

const STATUS_COLORS = {
  success: "green",
  opened: "blue",
  clicked: "purple",
  failed: "red",
  invalid: "red",
  bounced: "volcano",
  complained: "red",
  unsubscribed: "default",
};

// Recharts needs real hex/CSS colors, not antd Tag color keywords -- a
// small parallel map for just the lines worth plotting (raw send-attempt
// noise like "invalid" is folded into the table only, not charted).
const CHART_LINES = [
  { key: "success", name: "Sent", stroke: "#2E7D32" },
  { key: "opened", name: "Opened", stroke: "#1565C0" },
  { key: "clicked", name: "Clicked", stroke: "#6A1B9A" },
  { key: "bounced", name: "Bounced", stroke: "#E65100" },
  { key: "failed", name: "Failed", stroke: "#C62828" },
];

function EmailStatsChart({ organizationId }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const query = { days: 30 };
    if (organizationId) query.organizationId = organizationId;
    privateAdminGetApi("admin/email-logs/stats", query)
      .then((res) => {
        setSeries(res.data.result.series);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [organizationId]);

  return (
    <Card
      size="small"
      title="Sent vs. opened vs. clicked vs. bounced (last 30 days)"
      style={{ marginBottom: 16 }}
      loading={loading}
    >
      {series.length ? (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={series}>
            <CartesianGrid stroke="#E8ECEF" strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
            <Tooltip />
            <Legend />
            {CHART_LINES.map(({ key, name, stroke }) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={name}
                stroke={stroke}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Text type="secondary">No data in the last 30 days.</Text>
      )}
    </Card>
  );
}

export default function AdminEmailLogs() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [organizationId, setOrganizationId] = useState(undefined);
  const [status, setStatus] = useState(undefined);

  useEffect(() => {
    privateAdminGetApi("admin/organizations").then((res) => {
      setOrgs(res.data.result.records);
    });
  }, []);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const query = {};
    if (organizationId) query.organizationId = organizationId;
    if (status) query.status = status;
    privateAdminGetApi("admin/email-logs", query)
      .then((res) => {
        setRecords(res.data.result.records);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [organizationId, status]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns = [
    {
      title: "Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => new Date(v).toLocaleString(),
      width: 190,
    },
    {
      title: "Organization",
      dataIndex: "organizationName",
      key: "organizationName",
      render: (v) => v || "-",
    },
    {
      title: "Campaign",
      dataIndex: "campaignSubject",
      key: "campaignSubject",
      ellipsis: true,
    },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag color={STATUS_COLORS[v] || "default"}>{v}</Tag>,
    },
  ];

  return (
    <div>
      <Space
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Title level={5} style={{ marginBottom: 0 }}>
          Email logs
        </Title>
        <Space>
          <Select
            allowClear
            placeholder="Organization"
            style={{ width: 220 }}
            value={organizationId}
            onChange={setOrganizationId}
            options={orgs.map((o) => ({ value: o._id, label: o.name }))}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchLogs} />
        </Space>
      </Space>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        Every campaign email event across all organizations (send attempts,
        opens, clicks, bounces).
      </Text>
      <EmailStatsChart organizationId={organizationId} />
      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={records}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 900 }}
      />
    </div>
  );
}
